import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

interface FormulaMateContextValue {
  buildFormulaActive: boolean;
  setBuildFormulaActive: (active: boolean) => void;
  reviewRequest: { code: string } | null;
  openCodeReview: (code: string) => void;
}

const FormulaMateContext = createContext<FormulaMateContextValue>({
  buildFormulaActive: false,
  setBuildFormulaActive: () => {
    throw new Error(
      'FormulaMateProvider is required for Build Formula visibility.'
    );
  },
  reviewRequest: null,
  openCodeReview: () => {
    throw new Error('FormulaMateProvider is required to open a code review.');
  },
});

export function FormulaMateProvider({ children }: { children: ReactNode }) {
  const [buildFormulaActive, setBuildFormulaActive] = useState(false);
  const [reviewRequest, setReviewRequest] =
    useState<FormulaMateContextValue['reviewRequest']>(null);

  return (
    <FormulaMateContext.Provider
      value={{
        buildFormulaActive,
        setBuildFormulaActive,
        reviewRequest,
        openCodeReview: code => setReviewRequest({ code }),
      }}
    >
      {children}
    </FormulaMateContext.Provider>
  );
}

export const useFormulaMate = () => useContext(FormulaMateContext);

export function FormulaMateBuildFormulaScope() {
  const { setBuildFormulaActive } = useFormulaMate();
  useEffect(() => {
    setBuildFormulaActive(true);
    return () => setBuildFormulaActive(false);
  }, [setBuildFormulaActive]);
  return null;
}

export function FormulaMateReviewLink({
  code,
  children = 'Review with FormulaMate',
  ariaLabel = 'Review with FormulaMate',
}: {
  code: string;
  children?: ReactNode;
  ariaLabel?: string;
}) {
  const { openCodeReview } = useFormulaMate();

  return (
    <a
      href="#formulamate-chat"
      className="underline cursor-pointer rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 text-blue-400"
      onClick={event => {
        event.preventDefault();
        openCodeReview(code);
      }}
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
}

export function FormulaMateFixLink({ code }: { code: string }) {
  return (
    <FormulaMateReviewLink code={code} ariaLabel="Fix code with FormulaMate AI">
      Fix with FormulaMate
    </FormulaMateReviewLink>
  );
}
